import {
  ALL,
  CATEGORIES,
  DEFAULT_CURRENCY,
  SORT_LABELS,
  categoryChips,
  formatMoney,
  summarize,
  visibleItems,
  type CategoryFilter,
  type SortKey,
  type User,
  type View,
} from '@closetly/core';
import { useMemo, useState } from 'react';
import { useItemEditor } from '../lib/editor';
import { useItems, useMe, useOutfits, useSetBudget, willQueue } from '../lib/queries';
import { useToast } from '../lib/toast';
import { BudgetSheet } from './BudgetSheet';
import { ItemCard } from './ItemCard';
import { ItemTile } from './ItemTile';
import { ShareSheet } from './ShareSheet';
import { Summary } from './Summary';

type Layout = 'board' | 'tags';
const LAYOUT_KEY = 'closetly-layout';

// a per-device preference; storage may be unavailable (private mode)
function storedLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'tags' ? 'tags' : 'board';
  } catch {
    return 'board';
  }
}

export function WishlistPage({ user }: { user: User }) {
  const items = useItems(user.id);
  const outfits = useOutfits(user.id);
  const me = useMe(user);
  const setBudget = useSetBudget(user.id);
  const editor = useItemEditor();
  const toast = useToast();

  const [view, setView] = useState<View>('open');
  const [cat, setCat] = useState<CategoryFilter>(ALL);
  const [sort, setSort] = useState<SortKey>('new');
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [layout, setLayoutState] = useState<Layout>(storedLayout);
  const setLayout = (l: Layout) => {
    setLayoutState(l);
    try {
      localStorage.setItem(LAYOUT_KEY, l);
    } catch {
      // only a convenience
    }
  };

  const all = useMemo(() => items.data ?? [], [items.data]);
  const currency = me.data?.currency || DEFAULT_CURRENCY;
  const budgetCents = me.data?.budget_cents ?? 0;
  const summary = useMemo(() => summarize(all, budgetCents, currency), [all, budgetCents, currency]);
  const chips = useMemo(() => categoryChips(all, view), [all, view]);
  // a category with nothing left in this view falls back to "Alle"
  const activeCat = chips.some((c) => c.cat === cat) ? cat : ALL;
  const list = useMemo(() => visibleItems(all, { view, cat: activeCat, sort }), [all, view, activeCat, sort]);
  const shownCents = list.reduce((s, i) => s + i.price_cents, 0);

  // which outfits each piece belongs to, for the card
  const outfitNames = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const o of outfits.data ?? [])
      for (const id of o.items) m.set(id, [...(m.get(id) ?? []), o.name || 'Outfit']);
    return m;
  }, [outfits.data]);

  const save = (i: { id: string; name: string }) => editor.openSave({ type: 'item', id: i.id, name: i.name });

  function saveBudget(cents: number) {
    setBudgetOpen(false);
    setBudget.mutate(
      { uid: user.id, budget_cents: cents },
      { onError: () => toast('Speichern fehlgeschlagen. Versuch es nochmal.') },
    );
    toast(
      willQueue()
        ? 'Gespeichert. Wird gesendet, sobald du wieder online bist.'
        : cents
          ? 'Budget gespeichert'
          : 'Budget entfernt',
    );
  }

  return (
    <>
      <Summary
        summary={summary}
        currency={currency}
        onEditBudget={() => setBudgetOpen(true)}
        onShare={() => setSharing(true)}
      />

      <div className="controls">
        <div className="control-row">
        <div className="seg" role="group" aria-label="Ansicht">
          {(
            [
              ['open', 'Will ich'],
              ['bought', 'Gekauft'],
            ] as const
          ).map(([v, label]) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
              {label}
            </button>
          ))}
        </div>
        <div className="seg" role="group" aria-label="Darstellung">
          {(
            [
              ['board', 'Board'],
              ['tags', 'Liste'],
            ] as const
          ).map(([l, label]) => (
            <button key={l} type="button" aria-pressed={layout === l} onClick={() => setLayout(l)}>
              {label}
            </button>
          ))}
        </div>
        </div>
        <div className="chips" role="group" aria-label="Kategorie">
          {chips.map((c) => (
            <button
              key={c.cat}
              type="button"
              className="chip"
              aria-pressed={c.cat === activeCat}
              onClick={() => setCat(c.cat)}
            >
              {c.cat === ALL ? c.cat : `${c.cat} ${c.count}`}
            </button>
          ))}
        </div>
        <div className="sortrow">
          <span>{list.length > 0 && `${list.length} angezeigt, zusammen ${formatMoney(shownCents, currency)}`}</span>
          <label>
            Sortieren{' '}
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              {Object.entries(SORT_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <main className={layout === 'board' && list.length > 0 ? 'board' : 'list'}>
        {items.isPending ? (
          <div className="loading">Lade deine Liste…</div>
        ) : items.isError && !items.data ? (
          <div className="empty">
            <strong>Liste konnte nicht geladen werden</strong>
            <button type="button" className="btn" onClick={() => items.refetch()}>
              Nochmal versuchen
            </button>
          </div>
        ) : list.length === 0 ? (
          <EmptyState none={all.length === 0} view={view} />
        ) : layout === 'board' ? (
          list.map((i) => <ItemTile key={i.id} item={i} onOpen={editor.openItem} onSave={save} />)
        ) : (
          list.map((i) => (
            <ItemCard
              key={i.id}
              item={i}
              outfits={outfitNames.get(i.id) ?? []}
              onOpen={editor.openItem}
              onToggleBought={editor.toggleBought}
              onSave={save}
            />
          ))
        )}
      </main>

      <button
        className="fab"
        type="button"
        onClick={() => editor.openNew({ category: activeCat === ALL ? CATEGORIES[0] : activeCat })}
      >
        + Teil hinzufügen
      </button>

      {sharing && <ShareSheet uid={user.id} target={{ kind: 'list' }} onClose={() => setSharing(false)} />}
      {budgetOpen && (
        <BudgetSheet budgetCents={budgetCents} onSave={saveBudget} onClose={() => setBudgetOpen(false)} />
      )}
    </>
  );
}

function EmptyState({ none, view }: { none: boolean; view: View }) {
  if (none)
    return (
      <div className="empty">
        <strong>Noch nichts auf der Liste</strong>
        Tippe auf „Teil hinzufügen“, um dein erstes Wunschteil zu speichern.
      </div>
    );
  if (view === 'bought')
    return (
      <div className="empty">
        <strong>Noch nichts gekauft</strong>
        Markiere ein Teil als gekauft, dann landet es hier.
      </div>
    );
  return (
    <div className="empty">
      <strong>Hier ist nichts</strong>
      Wähle eine andere Kategorie oder füge ein Teil hinzu.
    </div>
  );
}
